import { Injectable, NotFoundException } from '@nestjs/common';
import { KnowledgeEntry, Prisma } from '@chat/prisma-client';
import { PrismaService } from '../prisma/prisma.service';
import { CreateKnowledgeEntryDto, UpdateKnowledgeEntryDto } from './dto/knowledge-entry.dto';

export interface UnansweredQuestion {
  question: string;
  askedAt: Date;
}

// The authoring side of the bot's FAQ — ScriptedChatResponder is the
// read-only retrieval half (retrieval/knowledge-index.ts), rebuilt from
// whatever's active here on its own short-TTL cache (see that file's
// comment on why writes here don't push an invalidation).
@Injectable()
export class KnowledgeService {
  constructor(private readonly prisma: PrismaService) {}

  async list(applicationId: string): Promise<KnowledgeEntry[]> {
    return this.prisma.knowledgeEntry.findMany({
      where: { applicationId },
      orderBy: { sortOrder: 'asc' },
    });
  }

  // What to write next, driven by real gaps instead of guessing: every bot
  // reply already tags itself matched:false when nothing scored above
  // UNCERTAIN_THRESHOLD (scripted-chat-responder.ts) — this just surfaces
  // that signal, pairing each miss with the shopper's actual question
  // (the preceding user message in the same session; the miss row itself
  // is only the generic fallback text, not informative on its own).
  async listUnanswered(applicationId: string, limit = 50): Promise<UnansweredQuestion[]> {
    const misses = await this.prisma.chatMessage.findMany({
      where: {
        role: 'assistant',
        session: { applicationId },
        metadata: { path: ['matched'], equals: false },
      },
      orderBy: { createdOn: 'desc' },
      take: limit,
      select: { sessionId: true, createdOn: true },
    });

    const results: UnansweredQuestion[] = [];
    for (const miss of misses) {
      const question = await this.prisma.chatMessage.findFirst({
        where: { sessionId: miss.sessionId, role: 'user', createdOn: { lt: miss.createdOn } },
        orderBy: { createdOn: 'desc' },
        select: { content: true },
      });
      if (question) results.push({ question: question.content, askedAt: miss.createdOn });
    }

    return results;
  }

  async create(applicationId: string, dto: CreateKnowledgeEntryDto): Promise<KnowledgeEntry> {
    return this.prisma.knowledgeEntry.create({
      data: {
        applicationId,
        question: dto.question,
        answer: dto.answer,
        keywords: dto.keywords,
        links: (dto.links ?? []) as unknown as Prisma.InputJsonValue,
        sortOrder: dto.sortOrder ?? 0,
      },
    });
  }

  async update(applicationId: string, id: string, dto: UpdateKnowledgeEntryDto): Promise<KnowledgeEntry> {
    await this.getOrThrow(applicationId, id);

    return this.prisma.knowledgeEntry.update({
      where: { id },
      data: {
        ...(dto.question !== undefined ? { question: dto.question } : {}),
        ...(dto.answer !== undefined ? { answer: dto.answer } : {}),
        ...(dto.keywords !== undefined ? { keywords: dto.keywords } : {}),
        ...(dto.links !== undefined ? { links: dto.links as unknown as Prisma.InputJsonValue } : {}),
        ...(dto.isActive !== undefined ? { isActive: dto.isActive } : {}),
        ...(dto.sortOrder !== undefined ? { sortOrder: dto.sortOrder } : {}),
      },
    });
  }

  async remove(applicationId: string, id: string): Promise<void> {
    await this.getOrThrow(applicationId, id);
    await this.prisma.knowledgeEntry.delete({ where: { id } });
  }

  // Never distinguishes "doesn't exist" from "belongs to another
  // application" — same id-probing defence as chat.service.ts's
  // getSessionOrThrow.
  private async getOrThrow(applicationId: string, id: string): Promise<KnowledgeEntry> {
    const entry = await this.prisma.knowledgeEntry.findUnique({ where: { id } });

    if (!entry || entry.applicationId !== applicationId) {
      throw new NotFoundException('Knowledge entry not found');
    }

    return entry;
  }
}
